import { useMemo, useEffect } from 'react';
import { cx } from '@emotion/css';
import {
  useQAPairVisible,
  useQAPairMeta,
  QAPairQuerySelectLimit,
  useQAPairSelectMode,
  QAPairSupportMessageTypes,
} from 'hooks/business/im/useQAPair';
import { QAPairCheckButton } from './QAPairCheckButton';
import { eventBus } from 'services/eventBus';
import { isNil, toNumber } from 'lodash-es';
import { LocalMessage } from 'hooks/business/conversation';
import { useTranslate } from '@tolgee/react';
import { useMemoizedFn } from 'ahooks';
import {
  useGetQAPairQueryAIReplyMsgMap,
  useSetQAPairQueryAIReplyMsgMap,
} from 'hooks/business/im/useQAPairAIReply';
import { useSetQAPairQueryHumanReplyMsgMap } from 'hooks/business/im/useQAPairHumanReply';
import { useChatContext } from 'views/Conversation/Chat/contexts/ChatContext';
import {
  useSetSelectedAnswerMsgIds,
  useSetSelectedQueryMsgIds,
  useIsQueryMsgSelected,
  useIsAnswerMsgSelected,
  useIsQueryMsgCountReachedLimit,
  useIsQueryAIReplyMsgSelected,
  useSelectedMeta,
} from 'hooks/business/im/useMessageSelect';
import { BuyerCheckButton } from './BuyerCheckButton';

import { MsgListItemProps } from './types';
import { MsgListItemWrapper } from './Wrapper';

const buyerCheckDisabledMessageTypes: LocalMessage['messageType'][] = ['IMAGE', 'VIDEO', 'STICKER'];

/**
 * 左侧消息，买家消息
 */
const MsgListItemLeft = (props: MsgListItemProps) => {
  const { isGalaxy, message, highlightMsg, children, getRef } = props;
  const { msgId: currentMsgId, messageType, sendTime } = message;
  const messageSendTime = toNumber(sendTime);

  const { t } = useTranslate();
  const { primaryKey } = useChatContext();
  const { rangeTimestamp } = useQAPairMeta();
  const qaPairVisible = useQAPairVisible();
  const selectMode = useQAPairSelectMode();
  const setSelectedKnowledgeQueryMsgIds = useSetSelectedQueryMsgIds(primaryKey, !isGalaxy);
  const getQAPairQueryAIReplyMsgMap = useGetQAPairQueryAIReplyMsgMap();

  const isQueryMsgSelected = useIsQueryMsgSelected(currentMsgId, primaryKey, !isGalaxy);
  // 只订阅「是否达到上限」的布尔值，而不是具体数量，减少不必要的重渲染
  const isQueryMsgCountReachedLimit = useIsQueryMsgCountReachedLimit(
    QAPairQuerySelectLimit,
    primaryKey,
    !isGalaxy,
  );

  const selectedMeta = useSelectedMeta(primaryKey);

  const isInRangeTimestamp = useMemo(() => {
    const selectedMetaRangeTimestamp = selectedMeta?.rangeTimestamp;
    console.log('selectedMetaRangeTimestamp', selectedMetaRangeTimestamp);
    if (!isNil(selectedMetaRangeTimestamp)) {
      return Boolean(messageSendTime >= selectedMetaRangeTimestamp);
    }

    return Boolean(rangeTimestamp && messageSendTime >= rangeTimestamp);
  }, [messageSendTime, rangeTimestamp, selectedMeta?.rangeTimestamp]);

  const checkButtonEnable = useMemo(() => {
    const msgTypes: LocalMessage['messageType'][] = ['TEXT', 'IMAGE', 'VIDEO', 'STICKER'];

    if (selectedMeta?.selectMode === 'buyer' || selectedMeta?.selectMode === 'both') {
      return msgTypes.includes(messageType) && isInRangeTimestamp;
    }

    if (isGalaxy || selectMode === 'single') {
      return false;
    }

    return qaPairVisible && msgTypes.includes(messageType) && isInRangeTimestamp;
  }, [
    selectedMeta?.selectMode,
    isGalaxy,
    selectMode,
    qaPairVisible,
    messageType,
    isInRangeTimestamp,
  ]);

  const checked = useMemo(() => {
    if (checkButtonEnable) {
      return isQueryMsgSelected;
    }

    return false;
  }, [checkButtonEnable, isQueryMsgSelected]);

  const disabledInfo = useMemo(() => {
    if (!checked) {
      if (buyerCheckDisabledMessageTypes.includes(messageType)) {
        return {
          disabled: true,
          tooltip: t('AI暂不支持分析买家发送的图片&视频问题'),
        };
      }

      if (isQueryMsgCountReachedLimit) {
        return {
          disabled: true,
          tooltip: t('至多可选x句', { x: QAPairQuerySelectLimit }),
        };
      }
    }

    return {
      disabled: false,
      tooltip: undefined,
    };
  }, [checked, messageType, isQueryMsgCountReachedLimit, t]);

  const handleLeftClick = useMemoizedFn(() => {
    if (!checkButtonEnable || disabledInfo.disabled) {
      return;
    }
    eventBus.emit('CLEAR_QAPAIR_COUNTDOWN_TIMER');

    setSelectedKnowledgeQueryMsgIds((prev) => {
      if (!checked && prev && prev.size >= QAPairQuerySelectLimit) {
        return prev;
      }

      const newMap = new Map(prev);
      if (checked) {
        newMap.delete(currentMsgId);
      } else {
        const relate = getQAPairQueryAIReplyMsgMap(currentMsgId);
        newMap.set(currentMsgId, new Set(relate));
      }
      return newMap;
    });
  });

  return (
    <MsgListItemWrapper
      message={message}
      highlightMsg={highlightMsg}
      getRef={getRef}
      onClick={handleLeftClick}
      className={cx({
        'bg-[#DFE0E3]': checked,
      })}
    >
      {checkButtonEnable && <BuyerCheckButton checked={checked} disabledInfo={disabledInfo} />}
      {children}
    </MsgListItemWrapper>
  );
};

/**
 * 右侧消息
 */
const MsgListItemRight = (props: MsgListItemProps) => {
  const { isGalaxy, message, highlightMsg, children, getRef } = props;

  const { msgId: currentMsgId, msgFromType, extraInfo, sendTime } = message;
  const messageSendTime = toNumber(sendTime);
  const reqMsgId = `${extraInfo?.reqMsgId ?? ''}`;
  const popupFor = `${extraInfo?.popupFor ?? ''}`;

  const { primaryKey } = useChatContext();
  const { rangeTimestamp } = useQAPairMeta();
  const qaPairVisible = useQAPairVisible();
  const selectMode = useQAPairSelectMode();
  const setQAPairQueryAIReplyMsgMap = useSetQAPairQueryAIReplyMsgMap();
  const setQAPairQueryHumanReplyMsgMap = useSetQAPairQueryHumanReplyMsgMap();
  const setSelectedKnowledgeQueryMsgIds = useSetSelectedQueryMsgIds(primaryKey, !isGalaxy);
  const getQAPairQueryAIReplyMsgMap = useGetQAPairQueryAIReplyMsgMap();
  const setSelectedKnowledgeAnswerMsgIds = useSetSelectedAnswerMsgIds(primaryKey, !isGalaxy);

  const isAnswerMsgSelected = useIsAnswerMsgSelected(currentMsgId, primaryKey, !isGalaxy);
  const isQueryAIReplyMsgSelected = useIsQueryAIReplyMsgSelected(
    currentMsgId,
    reqMsgId,
    primaryKey,
    !isGalaxy,
  );

  const isInRangeTimestamp = useMemo(() => {
    return Boolean(rangeTimestamp && messageSendTime >= rangeTimestamp);
  }, [messageSendTime, rangeTimestamp]);

  const isInMessageType = useMemo(() => {
    const { messageType, isFromOfficial, isAutoReply } = message;
    return QAPairSupportMessageTypes.includes(messageType) && !isFromOfficial && !isAutoReply;
  }, [message]);

  const checkButtonEnable = useMemo(() => {
    if (isGalaxy) {
      return false;
    }
    const { msgFromType } = message;

    const includesMsgFromType: LocalMessage['msgFromType'][] = ['ASSISTANT'];
    if (selectMode === 'multiple') {
      includesMsgFromType.push('BOT');
    }

    const isRightMessage = includesMsgFromType.includes(msgFromType) && isInMessageType;

    return qaPairVisible && isRightMessage && isInRangeTimestamp;
  }, [isGalaxy, message, selectMode, isInMessageType, qaPairVisible, isInRangeTimestamp]);

  const checked = useMemo(() => {
    if (checkButtonEnable) {
      if (msgFromType === 'BOT') {
        return isQueryAIReplyMsgSelected;
      }

      return isAnswerMsgSelected;
    }

    return false;
  }, [checkButtonEnable, msgFromType, isAnswerMsgSelected, isQueryAIReplyMsgSelected]);

  const handleRightClick = useMemoizedFn(() => {
    if (!checkButtonEnable) {
      return;
    }
    eventBus.emit('CLEAR_QAPAIR_COUNTDOWN_TIMER');

    if (msgFromType === 'BOT' && reqMsgId) {
      console.log('selectItem', reqMsgId, currentMsgId);
      setSelectedKnowledgeQueryMsgIds((prev) => {
        const newMap = new Map(prev);
        if (newMap.has(reqMsgId)) {
          // 删除 reqMsgId 对应的 set
          newMap.delete(reqMsgId);
        } else {
          const relate = getQAPairQueryAIReplyMsgMap(reqMsgId);
          newMap.set(reqMsgId, new Set(relate));
        }
        return newMap;
      });
      return;
    }

    setSelectedKnowledgeAnswerMsgIds((prev) => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.delete(currentMsgId);
      } else {
        newSet.add(currentMsgId);
      }
      return newSet;
    });
  });

  useEffect(() => {
    if (isGalaxy) {
      return;
    }

    if (msgFromType === 'BOT' && reqMsgId) {
      // 收集 Q 和 AI 回复关联关系 map
      setQAPairQueryAIReplyMsgMap(reqMsgId, currentMsgId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGalaxy, reqMsgId, msgFromType, currentMsgId]);

  useEffect(() => {
    if (isGalaxy) {
      return;
    }

    if (popupFor) {
      setQAPairQueryHumanReplyMsgMap(popupFor, currentMsgId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGalaxy, popupFor, currentMsgId]);

  return (
    <MsgListItemWrapper
      message={message}
      highlightMsg={highlightMsg}
      getRef={getRef}
      onClick={handleRightClick}
      className={cx(checkButtonEnable ? 'justify-between' : 'justify-end', {
        'bg-[#DFE0E3]': checked,
      })}
    >
      {checkButtonEnable && <QAPairCheckButton color="black" checked={checked} />}
      {children}
    </MsgListItemWrapper>
  );
};

export const MsgListItem = (props: MsgListItemProps) => {
  const { layout } = props;

  if (layout === 'left') {
    return <MsgListItemLeft {...props} />;
  } else {
    return <MsgListItemRight {...props} />;
  }
};
